import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { motion, PresenceContext, useAnimationControls, useReducedMotion } from 'framer-motion'
import { arriveMotion } from '@/src/renderer/lib/motion/arriveMotion'
import { revealSequence } from '@/src/renderer/lib/motion/revealSequence'
import { whenCalm } from '@/src/renderer/lib/motion/whenCalm'
import type { EmptyStateLeave, EmptyStateProps } from './EmptyState.types'
import './EmptyState.scss'

/**
 * Lässt die Ankunft **jedes Mal** spielen, wenn die Box zu sehen ist — und
 * gibt die drei Zustände zurück, die `motion` dafür braucht.
 *
 * Eine Bewegung läuft normalerweise beim Montieren des Knotens. Für den leeren
 * Zustand reicht das nicht: Der TabStack lässt inaktive Tabs montiert und
 * blendet sie per `display: none` aus — bei der Rückkehr auf einen Tab gibt es
 * also gar keinen Mount, und die Box stände einfach da.
 *
 * Der Beobachter meldet beide Richtungen, und **beide werden gebraucht**:
 *
 * - **Aus dem Blick** → sofort auf den Anfangswert zurückspulen. Ohne das
 *   behält die Box ihren Endzustand, während der Tab weg ist; beim
 *   Wiedereintritt ist sie im ersten Frame voll da, wird dann zurückgesetzt
 *   und kommt erst danach an. Sichtbar als Blitzen, Verschwinden, Ankommen.
 * - **In den Blick** → abspielen.
 *
 * Der Beobachter übernimmt auch den Mount: Beim `observe()` meldet er sofort,
 * ob das Element im Blick ist. Deshalb startet hier NICHTS zusätzlich beim
 * Montieren — das liefe sonst zweimal.
 *
 * Ohne `IntersectionObserver` (ältere Umgebungen, jsdom) bleibt der einmalige
 * Start beim Montieren; die Bewegung geht dann nur der Tabwechsel-Fall
 * verloren. *
 * **Gestartet wird erst, wenn es ruhig ist** (`whenCalm`, 25.09.2026). Beim
 * Wechsel des Lebensbereichs meldet der Beobachter „sichtbar" mitten in der
 * teuersten Arbeit — die Einblendung ruckelte. Bis zum Start bleibt die Box im
 * Anfangszustand, es blitzt also nichts.
 */
function useArrival(reduced: boolean, sequenced: boolean) {
  const ref = useRef<HTMLDivElement>(null)
  const controls = useAnimationControls()
  // Mit Illustration: Abfolge über Varianten (`revealSequence`) — die Box
  // schaltet nur `hidden` ↔ `shown`, die Kinder bringen ihren Takt mit. Die Box
  // selbst bewegt sich dann nicht, nur ihr Abgang (`leave`) blendet sie aus.
  const arrive = useMemo(
    () => (sequenced
      ? {
          initial: 'hidden',
          animate: 'shown',
          exit: { opacity: 0, transform: 'translateY(8px)', transition: { duration: reduced ? 0 : 0.2 } },
        }
      : arriveMotion({ reduced })),
    [reduced, sequenced]
  )

  // Der noch ausstehende Start — ein Abgang oder ein Wegsehen bricht ihn ab.
  const cancelStartRef = useRef<() => void>(() => {})

  useEffect(() => {
    const startWhenCalm = () => {
      cancelStartRef.current()
      cancelStartRef.current = whenCalm(() => void controls.start(arrive.animate as never))
    }

    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      startWhenCalm()
      return () => cancelStartRef.current()
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        // Zurückspulen, SOLANGE niemand hinsieht — nicht erst beim
        // Wiedererscheinen. Dort käme es einen Frame zu spät.
        cancelStartRef.current()
        controls.set(arrive.initial as never)
        return
      }
      startWhenCalm()
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      cancelStartRef.current()
    }
  }, [controls, arrive])

  /**
   * Denselben Weg rückwärts — und erst danach die Folge-Aktion. Der Beobachter
   * stört dabei nicht: Er meldet Sichtbarkeit im Layout, und die ändert sich
   * durch Deckkraft und Verschiebung nicht.
   */
  const leave = useCallback<EmptyStateLeave>(
    (then) => {
      cancelStartRef.current()
      void controls.start(arrive.exit as never).then(() => then?.())
    },
    [controls, arrive]
  )

  return { ref, controls, arrive, leave }
}

/**
 * Der leere Zustand einer Seite. Zwei Gestalten für denselben Inhalt —
 * `plain` und `card` (siehe `.claude/tasks/empty-states-ruhend-projekt.md`
 * und `.claude/tasks/empty-state-karte-und-tab-grund.md`).
 *
 * **`plain`** — Medaillon, Überschrift, zwei Zeilen, optional Knöpfe. Zwei
 * Entscheidungen stecken in der Gestalt:
 *
 * - **Weicher Kreis statt gestricheltem Rahmen.** Gestrichelt ist
 *   Werkzeug-Sprache (Ablegen, Platzhalter); hier wird niemand aufgefordert,
 *   etwas hineinzuziehen.
 * - **Lebensbereichs-Farbe (`--space-*`).** Der leere Zustand gehört sichtbar
 *   zu *diesem* Bereich — Arbeit sieht anders aus als Zuhause. Die Variablen
 *   werden zur Laufzeit gesetzt, die Komponente greift sie nur ab.
 *
 * **`card`** — ein weißer Kasten mit Kontur, aus dessen Oberkante das Motiv
 * herausragt; alles auf der Mittelachse. Die Form zitiert den In-App-Teaser der
 * Onboarding-Screens (Figma `↳ Empty State`, Variante F). Sie bringt ihren
 * eigenen abgesetzten Grund mit, damit der weiße Kasten sich abhebt; der Grund
 * endet mit dem leeren Zustand. Ein leeres Projekt zeigt also einen Kasten auf
 * abgesetzter Fläche, ein gefülltes Zeilen auf Weiß — der Unterschied ist ohne
 * Lesen da.
 *
 * @example
 * <EmptyState
 *   icon={<DormantOpenIcon size={40} />}
 *   title={t('dormant.emptyTitles.0')}
 *   text={t('dormant.emptyText')}
 * />
 *
 * @example
 * <EmptyState
 *   variant="card"
 *   motif={<ProjectEmptyMotifsIcon />}
 *   title={t('project.emptyTitles.0')}
 *   text={t('project.emptyText')}
 *   actions={<Button onClick={onCreateTask}>{t('listToolbar.addTask')}</Button>}
 * />
 */
/*
  **Abgeschirmt von einer Hülle darüber** (25.09.2026): Kurz notiert legt seine
  Liste in ein `AnimatePresence initial={false}`, und das schaltet den
  Anfangszustand aller Kinder ab — das Bild stand sofort voll da und sprang
  erst beim Start zurück auf klein. Ein leerer `PresenceContext` hebt das für
  den leeren Zustand auf; seinen Auftritt und Abgang steuert er ohnehin selbst
  (`useArrival`, `leave`).
*/
export const EmptyState: React.FC<EmptyStateProps> = (props) => (
  <PresenceContext.Provider value={null}>
    <EmptyStateInner {...props} />
  </PresenceContext.Provider>
)

const EmptyStateInner: React.FC<EmptyStateProps> = (props) => {
  const { title, text, actions, actionsLayout = 'row', titleAside, actionsKey, className } = props
  const variant = props.variant ?? 'plain'
  const reduced = useReducedMotion() ?? false
  const sequenced = props.variant !== 'card' && Boolean(props.illustration)
  const { ref, controls, arrive, leave } = useArrival(reduced, sequenced)
  const reveal = useMemo(() => revealSequence({ reduced }), [reduced])
  // Neue Knöpfe (`actionsKey`) kommen für sich herein, wie beim Öffnen. Danach hängen sie wieder an der Box —
  // sonst spielten sie nach einem Tabwechsel nicht mit (`useArrival` spult nur, was ihr folgt).
  const again = useMemo(() => revealSequence({ reduced, startAt: 'actions' }), [reduced])
  const [shownActionsKey, setShownActionsKey] = useState(actionsKey)
  const [replaying, setReplaying] = useState(false)
  if (actionsKey !== shownActionsKey) {
    setShownActionsKey(actionsKey)
    setReplaying(true)
  }
  const rootClass = `empty-state empty-state--${variant}${className ? ` ${className}` : ''}`
  // `exit` wirkt nur, wo ein `AnimatePresence` darüber steht. Über dem leeren
  // Zustand steht keins — dort treibt der Knopf den Abgang selbst, über
  // `leave`. Die Prop bleibt trotzdem stehen, für den Tag, an dem eins kommt.
  const motionProps = { initial: arrive.initial, animate: controls, exit: arrive.exit }
  // Als Funktion übergeben, bekommen die Knöpfe `leave` in die Hand: erst
  // hinausfahren, dann handeln.
  const actionRow = typeof actions === 'function' ? actions(leave) : actions
  const actionsClass = `empty-state__actions${actionsLayout === 'stack' ? ' empty-state__actions--stack' : ''}`

  // Auf `props.variant` geprüft, nicht auf die lokale Variable: nur so grenzt
  // TypeScript die Union ein und gibt `props.motif` frei.
  if (props.variant === 'card') {
    return (
      <motion.div ref={ref} className={rootClass} {...motionProps}>
        <div className="empty-state__motif" aria-hidden="true">
          {props.motif}
        </div>
        <div className="empty-state__card">
          {withAside(<h2 className="empty-state__title">{title}</h2>, titleAside)}
          <p className="empty-state__text">{text}</p>
          {actions && <div className={actionsClass}>{actionRow}</div>}
        </div>
      </motion.div>
    )
  }

  // Mit Illustration: eins nach dem anderen (`revealSequence`).
  if (props.illustration) {
    return (
      <motion.div ref={ref} className={rootClass} {...motionProps}>
        <motion.div className="empty-state__illustration" aria-hidden="true" variants={reveal.illustration}>
          {props.illustration}
        </motion.div>
        {titleAside ? (
          <motion.div className="empty-state__title-row" variants={reveal.title}>
            <h2 className="empty-state__title">{title}</h2>
            {titleAside}
          </motion.div>
        ) : (
          <motion.h2 className="empty-state__title" variants={reveal.title}>{title}</motion.h2>
        )}
        <motion.p className="empty-state__text" variants={reveal.text}>{text}</motion.p>
        {actions && (
          <motion.div
            key={actionsKey}
            className={actionsClass}
            variants={replaying ? again.actions : reveal.actions}
            {...(replaying ? { initial: 'hidden', animate: 'shown', onAnimationComplete: () => setReplaying(false) } : {})}
          >
            {actionRow}
          </motion.div>
        )}
      </motion.div>
    )
  }

  return (
    <motion.div ref={ref} className={rootClass} {...motionProps}>
      <div className="empty-state__medallion" aria-hidden="true">
        <span className="empty-state__icon">{props.icon}</span>
      </div>
      {withAside(<h2 className="empty-state__title">{title}</h2>, titleAside)}
      <p className="empty-state__text">{text}</p>
      {actions && <div className={actionsClass}>{actionRow}</div>}
    </motion.div>
  )
}

function withAside(heading: React.ReactElement, aside: React.ReactNode) {
  if (!aside) return heading
  return (
    <div className="empty-state__title-row">
      {heading}
      {aside}
    </div>
  )
}
